import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { IFile } from '@repo/toolkit/types'
import Button from '@repo/ui/button'
import { useForm } from 'react-hook-form'

import useTranslate from '../../../lib/hooks/use-translate'
import { Staff } from '../../../lib/types/models/staff/staff'
import { StaffInput } from '../../../lib/types/models/staff/staff-input'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import TranslatedForm from '../../elements/forms/form'
import StaffPersonalSpaceAdminstrativeForm from './staff-personal-space-adminstrative-form'
import StaffPersonalSpaceBankInformationForm from './staff-personal-space-bank-information-form'
import StaffPersonalSpaceEmergencyForm from './staff-personal-space-emergency-form'
import StaffPersonalSpaceInformationForm from './staff-personal-space-information-form'
import StaffPersonalSpaceProfessionalDetailsForm from './staff-personal-space-professional-details-form'
import { staffPersonalSpaceFormSchema } from './utils/staff-personal-space-schema'

type Props = {
  defaultValues?: Partial<StaffInput> | undefined
  isLoading?: boolean
  disabled?: boolean
  staff?: Pick<Staff, 'id' | 'qualifications' | 'picture' | 'position'>
  handleSubmit: (values: StaffInput) => void
  onUpdateProfilePicture: (file: IFile) => void
}

const StaffPersonalSpaceForm = ({
  defaultValues,
  handleSubmit: onSubmit,
  isLoading,
  disabled,
  staff,
  onUpdateProfilePicture
}: Props) => {
  const { currentLanguage, t } = useTranslate()

  const form = useForm<StaffInput>({
    resolver: zodResolver(staffPersonalSpaceFormSchema(currentLanguage, t)),
    defaultValues,
    mode: FORM_MODE,
    disabled
  })

  return (
    <TranslatedForm<StaffInput> form={form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className={cn({ 'pointer-events-none': isLoading || disabled })}
      >
        <div className="flex flex-col gap-4 ">
          <StaffPersonalSpaceInformationForm
            disabled={disabled}
            picture={staff?.picture}
            onUpdateProfilePicture={onUpdateProfilePicture}
          />
          <StaffPersonalSpaceProfessionalDetailsForm staff={staff} />
          <StaffPersonalSpaceAdminstrativeForm disabled={disabled} staff={staff} />
          <StaffPersonalSpaceBankInformationForm />
          <StaffPersonalSpaceEmergencyForm />
        </div>
        <div className="flex w-full justify-end mt-4">
          <Button
            className="mobile:w-full mobile:mb-6"
            type="submit"
            label={t('button.submit')}
            loading={isLoading}
            disabled={disabled}
          />
        </div>
      </form>
    </TranslatedForm>
  )
}

export default StaffPersonalSpaceForm
